import { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Fab from "@mui/material/Fab";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { SelectChangeEvent } from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SubjectIcon from "@mui/icons-material/Subject";
import ReplayIcon from "@mui/icons-material/Replay";
import IconButton from "@mui/material/IconButton";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE } from "../../constants/pdm";
import { routePaths } from "../../routePaths";
import type {
  Deployment,
  DeploymentStatus,
  DeploymentTemplateOption,
} from "../../types/pdm.types";
import { getDeployments, startRetryDeployment } from "../../services/pdmApi.service";
import { getAvailableNetworks } from "../../services/deploymentsManagement.service";
import { DeploymentLog } from "../DeploymentLogs";
import { DeploymentDetailsDialog } from "./DeploymentDetailsDialog";
import { useDeploymentTemplateOptions } from "./useDeploymentTemplateOptions";

function statusColor(
  status: DeploymentStatus,
): "success" | "warning" | "info" | "error" | "default" {
  switch (status) {
    case "success":
      return "success";
    case "pending":
      return "warning";
    case "in_progress":
      return "info";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

export default function DeploymentsView() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const refreshToken = searchParams.get("refresh");
  const [draftPage, setDraftPage] = useState(0);
  const [retryingById, setRetryingById] = useState<Record<string, boolean>>({});
  const [retryErrorMessage, setRetryErrorMessage] = useState<string | null>(null);
  const [retrySkippedMessage, setRetrySkippedMessage] = useState<string | null>(
    null,
  );
  const [retryDialog, setRetryDialog] = useState<{
    deploymentId: string;
    state: "sending" | "started" | "skipped";
  } | null>(null);
  const [draftFilters, setDraftFilters] = useState<{
    status: "" | DeploymentStatus;
    network: string;
    deploymentTemplate: DeploymentTemplateOption | null;
  }>({ status: "", network: "", deploymentTemplate: null });
  const [applied, setApplied] = useState<{
    page: number;
    status: "" | DeploymentStatus;
    network: string;
    deploymentTemplateId?: string;
  }>({ page: 0, status: "", network: "", deploymentTemplateId: undefined });
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(
    null,
  );
  const logDialogOpen = Boolean(selectedDeployment);
  const [selectedDeploymentIdForDetails, setSelectedDeploymentIdForDetails] =
    useState<string | null>(null);
  const detailsDialogOpen = Boolean(selectedDeploymentIdForDetails);

  const deploymentsQuery = useQuery({
    queryKey: [
      "deployments",
      applied.page,
      applied.status,
      applied.network,
      applied.deploymentTemplateId,
    ],
    queryFn: () =>
      getDeployments({
        offset: applied.page * PAGE_SIZE,
        limit: PAGE_SIZE,
        status: applied.status || undefined,
        network: applied.network || undefined,
        deploymentTemplateId: applied.deploymentTemplateId,
      }),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (!refreshToken) return;
    setDraftPage(0);
    setDraftFilters({ status: "", network: "", deploymentTemplate: null });
    setApplied({ page: 0, status: "", network: "", deploymentTemplateId: undefined });
    void queryClient.invalidateQueries({ queryKey: ["deployments"] });
    void deploymentsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const rows = useMemo(() => deploymentsQuery.data?.items ?? [], [deploymentsQuery.data]);
  const total = useMemo(() => deploymentsQuery.data?.total ?? 0, [deploymentsQuery.data]);

  const networksQuery = useQuery({
    queryKey: ["available-networks", "deployments"],
    queryFn: getAvailableNetworks,
  });

  const networkOptions = useMemo(
    () => (networksQuery.data ?? []).map((n) => n.id),
    [networksQuery.data],
  );

  const templateOptionsQuery = useDeploymentTemplateOptions();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setApplied({
      page: draftPage,
      status: draftFilters.status,
      network: draftFilters.network,
      deploymentTemplateId: draftFilters.deploymentTemplate?.id,
    });
  };

  const handleRetry = (deploymentId: string) => {
    if (retryingById[deploymentId]) return;
    setRetryingById((p) => ({ ...p, [deploymentId]: true }));
    setRetryDialog({ deploymentId, state: "sending" });

    void startRetryDeployment(deploymentId, { keepalive: true })
      .then((result) => {
        if (result?.status === "skipped") {
          setRetryDialog({ deploymentId, state: "skipped" });
          setRetrySkippedMessage(
            result.message?.trim()
              ? result.message
              : "The retry request was skipped because another deployment is currently running.",
          );
          return;
        }

        // Now that backend accepted the request, update the UI immediately.
        setRetryDialog({ deploymentId, state: "started" });
        queryClient.setQueriesData({ queryKey: ["deployments"] }, (prev) => {
          if (!prev || typeof prev !== "object") return prev;
          const data = prev as { items?: Deployment[]; total?: number };
          if (!Array.isArray(data.items)) return prev;
          return {
            ...data,
            items: data.items.map((d) =>
              d.id === deploymentId ? { ...d, status: "in_progress" } : d,
            ),
          };
        });
        void queryClient.invalidateQueries({ queryKey: ["deployments"] });
        void deploymentsQuery.refetch();
      })
      .catch((err) => {
        const message =
          err instanceof Error && err.message.trim()
            ? err.message
            : "There was a problem initiating the retry.";
        setRetryErrorMessage(message);
      })
      .finally(() => {
        setRetryingById((p) => {
          const next = { ...p };
          delete next[deploymentId];
          return next;
        });
      });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ color: "brand.main" }}>
        Deployments
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Recent DSApps deployments.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ width: "100%" }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ alignItems: { md: "center" } }}
          >
            <FormControl size="small" fullWidth>
              <InputLabel id="deployments-status-label">Status</InputLabel>
              <Select
                labelId="deployments-status-label"
                label="Status"
                value={draftFilters.status}
                onChange={(e: SelectChangeEvent) => {
                  setDraftPage(0);
                  setDraftFilters((p) => ({
                    ...p,
                    status: e.target.value as "" | DeploymentStatus,
                  }));
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In progress</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="deployments-network-label">Network</InputLabel>
              <Select
                labelId="deployments-network-label"
                label="Network"
                value={draftFilters.network}
                onChange={(e: SelectChangeEvent) => {
                  setDraftPage(0);
                  setDraftFilters((p) => ({
                    ...p,
                    network: e.target.value,
                  }));
                }}
              >
                <MenuItem value="">All</MenuItem>
                {networksQuery.isLoading && (
                  <MenuItem value="" disabled>
                    Loading…
                  </MenuItem>
                )}
                {networksQuery.isError && (
                  <MenuItem value="" disabled>
                    Unable to load networks
                  </MenuItem>
                )}
                {networkOptions.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              options={templateOptionsQuery.options}
              value={draftFilters.deploymentTemplate}
              loading={templateOptionsQuery.isLoading}
              onChange={(_, next) => {
                setDraftPage(0);
                setDraftFilters((p) => ({
                  ...p,
                  deploymentTemplate: next,
                }));
              }}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              getOptionLabel={(o) => o.name}
              fullWidth
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Template"
                  placeholder="All"
                  error={templateOptionsQuery.isError}
                  helperText={
                    templateOptionsQuery.isError
                      ? "Unable to load templates"
                      : undefined
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {templateOptionsQuery.isLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{ whiteSpace: "nowrap" }}
            >
              Search
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              sx={{ whiteSpace: "nowrap" }}
              onClick={() => void deploymentsQuery.refetch()}
              disabled={deploymentsQuery.isFetching}
            >
              Refresh
            </Button>
          </Stack>
        </Box>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Network</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const templateName = row.deploymentTemplate?.name?.trim();
              return (
                <TableRow key={row.id} hover>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>
                    {templateName ? (
                      <Typography variant="body2" component="span">
                        {templateName}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        component="span"
                        color="text.secondary"
                      >
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status.replace(/_/g, " ")}
                      color={statusColor(row.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.network}</TableCell>
                  <TableCell>
                    {new Date(row.createdAt).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ justifyContent: "flex-end" }}
                    >
                      {row.status === "failed" ? (
                        <Tooltip title="Retry deployment">
                          <span>
                            <IconButton
                              size="small"
                              aria-label="Retry deployment"
                              onClick={() => void handleRetry(row.id)}
                              disabled={Boolean(retryingById[row.id])}
                            >
                              <ReplayIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Details">
                        <IconButton
                          size="small"
                          aria-label="details"
                          onClick={() => setSelectedDeploymentIdForDetails(row.id)}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Logs">
                        <IconButton
                          size="small"
                          aria-label="logs"
                          onClick={() => setSelectedDeployment(row)}
                        >
                          <SubjectIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {!rows.length && !deploymentsQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">
                    No deployments match the current search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={draftPage}
          onPageChange={(_, next) => setDraftPage(next)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
        />
      </TableContainer>
      <Fab
        color="primary"
        variant="extended"
        sx={{ position: "fixed", bottom: 24, right: 24 }}
        onClick={() => navigate(routePaths.deploymentCreate)}
      >
        <AddIcon sx={{ mr: 1 }} />
        Deploy a new ds-App
      </Fab>

      <Dialog
        open={logDialogOpen}
        onClose={() => setSelectedDeployment(null)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "background.default",
          },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 1, pr: 1 }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              Deployment logs
            </Typography>
            <Typography color="text.secondary" variant="body2" noWrap>
              {selectedDeployment?.name ?? "—"}
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={() => setSelectedDeployment(null)}
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 2,
            overflow: "hidden",
          }}
        >
          {selectedDeployment && <DeploymentLog deployment={selectedDeployment} />}
        </DialogContent>
      </Dialog>

      <DeploymentDetailsDialog
        open={detailsDialogOpen}
        deploymentId={selectedDeploymentIdForDetails}
        onClose={() => setSelectedDeploymentIdForDetails(null)}
      />

      <Dialog
        open={Boolean(retryErrorMessage)}
        onClose={() => setRetryErrorMessage(null)}
        aria-labelledby="retry-error-title"
      >
        <DialogTitle id="retry-error-title">Unable to retry deployment</DialogTitle>
        <DialogContent dividers>
          <Typography>{retryErrorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setRetryErrorMessage(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(retrySkippedMessage)}
        onClose={() => setRetrySkippedMessage(null)}
        aria-labelledby="retry-skipped-title"
      >
        <DialogTitle id="retry-skipped-title">Retry skipped</DialogTitle>
        <DialogContent dividers>
          <Typography>{retrySkippedMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setRetrySkippedMessage(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(retryDialog)}
        onClose={() => setRetryDialog(null)}
        aria-labelledby="retry-dialog-title"
      >
        <DialogTitle id="retry-dialog-title">
          {retryDialog?.state === "started"
            ? "Retry started"
            : retryDialog?.state === "skipped"
              ? "Retry skipped"
              : "Retry request sent"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1}>
            <Typography>
              Deployment Id:
              <br />
              <strong>{retryDialog?.deploymentId ?? "—"}</strong>
            </Typography>
            <Typography>
              Status:{" "}
              <strong>
                {retryDialog?.state === "started"
                  ? "in_progress"
                  : retryDialog?.state === "skipped"
                    ? "failed"
                    : "sending"}
              </strong>
            </Typography>
            {retryDialog?.state === "sending" ? (
              <Typography color="text.secondary">
                Waiting for the server to confirm the retry request…
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setRetryDialog(null);
              void queryClient.invalidateQueries({ queryKey: ["deployments"] });
              void deploymentsQuery.refetch();
            }}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
